import React, { useEffect, useMemo, useState } from 'react';
import '../../styles/Modals.css';

const DEFAULT_MESSAGE = 'Please share the status';

/**
 * Reminder composer for a task. Lets the sender edit the reminder message and,
 * when a task has more than one assignee, pick exactly who should receive it.
 *
 * Props:
 *  - isOpen        : boolean
 *  - taskName      : string
 *  - assignees     : Array<{ id, name, email }>  (normalised by the caller)
 *  - currentUserId : string  (excluded — the sender never reminds themselves)
 *  - sending       : boolean
 *  - onClose       : () => void
 *  - onSubmit      : ({ note, recipientIds }) => void | Promise
 */
const TaskReminderModal = ({
  isOpen,
  taskName,
  assignees = [],
  currentUserId,
  sending = false,
  onClose,
  onSubmit,
}) => {
  // The sender can't remind themselves (backend skips the actor), so drop them.
  const selectableAssignees = useMemo(
    () => assignees.filter((a) => a?.id && String(a.id) !== String(currentUserId || '')),
    [assignees, currentUserId]
  );

  const [note, setNote] = useState(DEFAULT_MESSAGE);
  const [selectedIds, setSelectedIds] = useState([]);

  // Reset the form each time the modal opens for a (possibly different) task.
  useEffect(() => {
    if (isOpen) {
      setNote(DEFAULT_MESSAGE);
      setSelectedIds(selectableAssignees.map((a) => String(a.id)));
    }
  }, [isOpen, selectableAssignees]);

  if (!isOpen) return null;

  const toggle = (id) => {
    const key = String(id);
    setSelectedIds((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  const multiple = selectableAssignees.length > 1;
  const noOneToRemind = selectableAssignees.length === 0;
  const canSend = !sending && !noOneToRemind && selectedIds.length > 0;

  const handleSend = () => {
    if (!canSend) return;
    // When there's only one possible recipient we still send their id explicitly.
    onSubmit({ note: note.trim(), recipientIds: selectedIds });
  };

  return (
    <div className="modal-overlay" onClick={sending ? undefined : onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '460px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#2d3748' }}>Send Reminder</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close" disabled={sending}>
            ×
          </button>
        </div>

        {taskName && (
          <div style={{ fontSize: '13px', color: '#718096', marginBottom: '14px' }}>
            Task: <span style={{ color: '#2d3748', fontWeight: 600 }}>{taskName}</span>
          </div>
        )}

        {noOneToRemind ? (
          <div style={{ fontSize: '14px', color: '#c53030', background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: '6px', padding: '12px', marginBottom: '20px' }}>
            {assignees.length === 0
              ? 'This task has no assignees to remind.'
              : 'You are the only assignee on this task — there is no one else to remind.'}
          </div>
        ) : (
          <>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#4a5568', marginBottom: '6px' }}>
              Message
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Write a reminder message..."
              disabled={sending}
              style={{
                width: '100%',
                fontSize: '14px',
                color: '#2d3748',
                padding: '10px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                resize: 'vertical',
                fontFamily: 'inherit',
                marginBottom: '16px',
                boxSizing: 'border-box',
              }}
            />

            {multiple && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#4a5568' }}>
                    Send to ({selectedIds.length}/{selectableAssignees.length})
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedIds(
                        selectedIds.length === selectableAssignees.length
                          ? []
                          : selectableAssignees.map((a) => String(a.id))
                      )
                    }
                    disabled={sending}
                    style={{ background: 'none', border: 'none', color: '#667eea', fontSize: '12px', fontWeight: 600, cursor: 'pointer', padding: 0 }}
                  >
                    {selectedIds.length === selectableAssignees.length ? 'Clear all' : 'Select all'}
                  </button>
                </div>
                <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                  {selectableAssignees.map((a) => {
                    const key = String(a.id);
                    const checked = selectedIds.includes(key);
                    return (
                      <label
                        key={key}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                      >
                        <input type="checkbox" checked={checked} onChange={() => toggle(key)} disabled={sending} />
                        <span style={{ fontSize: '13px', color: '#2d3748' }}>
                          {a.name}
                          {a.email ? <span style={{ color: '#a0aec0' }}> · {a.email}</span> : null}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {!multiple && (
              <div style={{ fontSize: '13px', color: '#718096', marginBottom: '20px' }}>
                Reminder will be sent to{' '}
                <span style={{ color: '#2d3748', fontWeight: 600 }}>{selectableAssignees[0]?.name}</span>.
              </div>
            )}
          </>
        )}

        <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            style={{ padding: '10px 20px', background: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: '6px', cursor: sending ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '13px' }}
          >
            Cancel
          </button>
          {!noOneToRemind && (
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: canSend ? 'pointer' : 'not-allowed',
                fontWeight: 600,
                fontSize: '13px',
                opacity: canSend ? 1 : 0.6,
              }}
            >
              {sending ? 'Sending...' : 'Send Reminder'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskReminderModal;
